import {
  APP_INITIALIZER,
  ApplicationConfig,
  isDevMode,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { authFeature, AuthEffects } from './features/auth/state';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { forbiddenInterceptor } from './core/interceptors/forbidden.interceptor';
import { AuthSessionSyncService } from './core/services/auth-session-sync.service';
import { TokenRenewalSchedulerService } from './core/services/token-renewal-scheduler.service';
import {
  applicantFieldsFeature,
  ApplicantFieldsEffects,
  preEnrollmentDocumentsFeature,
  PreEnrollmentDocumentsEffects,
} from './features/pre-inscripcion/state';
import { settingsFeature, SettingsEffects } from './features/settings/state';
import { i18nFeature, I18nEffects } from './shared/i18n';

const initAuthSessionSync =
  (authSessionSync: AuthSessionSyncService, tokenRenewal: TokenRenewalSchedulerService) => () => {
    authSessionSync.start();
    tokenRenewal.start();
  };

export const I18N_PREFIX = '/i18n/';
export const I18N_SUFFIX = '.json';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, forbiddenInterceptor])),
    provideStore({
      [authFeature.name]: authFeature.reducer,
      [i18nFeature.name]: i18nFeature.reducer,
      [applicantFieldsFeature.name]: applicantFieldsFeature.reducer,
      [preEnrollmentDocumentsFeature.name]: preEnrollmentDocumentsFeature.reducer,
      [settingsFeature.name]: settingsFeature.reducer,
    }),
    provideEffects([
      AuthEffects,
      I18nEffects,
      ApplicantFieldsEffects,
      PreEnrollmentDocumentsEffects,
      SettingsEffects,
    ]),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideTranslateService({ fallbackLang: 'es' }),
    provideTranslateHttpLoader({ prefix: I18N_PREFIX, suffix: I18N_SUFFIX }),
    {
      provide: APP_INITIALIZER,
      useFactory: initAuthSessionSync,
      deps: [AuthSessionSyncService, TokenRenewalSchedulerService],
      multi: true,
    },
  ],
};
